import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  isPerformanceVideo,
  performanceVimeoEmbed,
} from "@/lib/performanceVideo";
import { RoutineTrimPlayer, type TrimPlayerHandle } from "./RoutineTrimPlayer";
import { RoutineTrimTimeline } from "./RoutineTrimTimeline";
import { ArrowLeft, CheckCircle2, Scissors, AlertCircle } from "lucide-react";
import { AdminPanelHeader } from "./AdminPanelHeader";
import { CompleteRoutineSection } from "@/components/playlist/CompleteRoutineSection";
import { useRoutine } from "@/hooks/useRoutine";
import { reviewRoutine, getRoutine, routineRequest } from "@/lib/routineApi";
import { generateRoutine, useRoutineTask } from "@/lib/routineTasks";
import {
  formatTimestamp,
  parseTimestamp,
  validateTrim,
} from "@/lib/routineTypes";
import type {
  RoutineClip,
  RoutineVideoChoice,
  RoutineSource,
} from "@/lib/routineTypes";

type ClipDraft = { valid: boolean; save: () => Promise<void> };
type Performance = { id: string; name: string; organizer: string };
const button =
  "rounded-lg bg-[#762a5c] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50";
const input =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-[#762a5c]";

export function PerformancesPanel() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Performance[]>([]);
  const [selected, setSelected] = useState<Performance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const load = async (offset = 0) => {
    setLoading(true);
    try {
      const result = await routineRequest<{ performances: Performance[] }>(
        "list",
        { offset },
      );
      setRows((prev) =>
        offset ? [...prev, ...result.performances] : result.performances,
      );
      setHasMore(result.performances.length === 50);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load performances.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  if (selected)
    return (
      <PerformanceEditor
        performance={selected}
        onBack={() => setSelected(null)}
        onGenerated={() => navigate(`/app/dashboard/playlist?playlist_id=${encodeURIComponent(selected.id)}`)}
      />
    );
  return (
    <div className="space-y-5">
      <AdminPanelHeader title="Final Video Editor" />
      <p className="text-sm text-gray-600">
        Review each tutorial’s Final Video and choose the portion to use in that
        performance. No trim is selected automatically.
      </p>
      <input
        aria-label="Search loaded performances"
        placeholder="Search loaded performances or organizers"
        className={input}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {error && (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {error}
          <button className="ml-3 underline" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}
      <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
        {rows
          .filter((r) =>
            `${r.name} ${r.organizer}`
              .toLowerCase()
              .includes(search.toLowerCase()),
          )
          .map((row) => (
            <button
              type="button"
              key={row.id}
              onClick={() => setSelected(row)}
              className="flex w-full items-center justify-between gap-4 p-5 text-left hover:bg-[#fcf7fa]"
            >
              <span>
                <span className="block font-semibold text-[#321b29]">
                  {row.name}
                </span>
                <span className="mt-1 block text-sm text-gray-500">
                  {row.organizer}
                </span>
              </span>
              <span className="whitespace-nowrap text-sm font-semibold text-[#762a5c]">
                Review routine →
              </span>
            </button>
          ))}
      </div>
      {loading && (
        <p role="status" className="text-sm text-gray-500">
          Loading performances…
        </p>
      )}
      {!loading && !rows.length && !error && <p>No performances yet.</p>}
      {hasMore && (
        <button
          className={button}
          disabled={loading}
          onClick={() => void load(rows.length)}
        >
          Load more performances
        </button>
      )}
    </div>
  );
}

export function PerformanceEditor({
  performance,
  onBack,
  onGenerated,
  backLabel = "All performances",
}: {
  performance: Performance;
  onBack: () => void;
  onGenerated?: () => void;
  backLabel?: string;
}) {
  const { data, error, refresh } = useRoutine(performance.id);
  const [drafts, setDrafts] = useState<Record<string, ClipDraft>>({});
  const task = useRoutineTask();
  const view = useRef(0);
  useEffect(() => () => { view.current++; }, [performance.id]);
  const registerDraft = useCallback((id: string, draft: ClipDraft) => {
    setDrafts((previous) => ({ ...previous, [id]: draft }));
  }, []);
  const valid =
    !!data?.clips.length &&
    data.clips.every((clip) => drafts[clip.entry_id]?.valid);
  const generate = async () => {
    if (!data || !valid || task?.busy) return;
    const pending = data.clips.map((clip) => drafts[clip.entry_id]!);
    const startedInView = view.current;
    try {
      await generateRoutine(data, async () => {
        for (const draft of pending) await draft.save();
        return getRoutine(performance.id);
      });
      // Return after browser work finishes; Vimeo continues processing remotely.
      // Leaving this editor while it runs must not cause a later redirect.
      if (startedInView === view.current && onGenerated) {
        onGenerated();
        return;
      }
    } catch {
      /* the persistent progress panel shows the error */
    }
    if (startedInView === view.current) await refresh();
  };
  if (data?.can_edit === false) return (
    <div className="space-y-4">
      <button type="button" className="text-[#762a5c] underline" onClick={onBack}>{backLabel}</button>
      <p role="alert">Tutorial access has expired or is unavailable. Restore access to all tutorials in this routine before editing its final video.</p>
    </div>
  );
  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-semibold text-[#762a5c]"
      >
        <ArrowLeft size={16} />
        {backLabel}
      </button>
      <div>
        <AdminPanelHeader title={data?.name ?? performance.name} />
      </div>
      <div className="rounded-xl border border-[#e7d4e0] bg-[#fcf5f9] p-4 text-sm text-[#653651]">
        Select the portion of each performance video below, then click Generate
        final video. Your ranges apply only to this routine.
      </div>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {!data && !error && <p>Loading tutorials…</p>}
      {data?.clips.map((clip, index) => (
        <ClipEditor
          key={`${clip.entry_id}:${clip.source_revision}:${clip.start}:${clip.end}:${clip.reviewed}`}
          playlistId={performance.id}
          clip={clip}
          index={index}
          videos={
            data.videos?.filter(
              (v) => v.course_id === clip.course_id && isPerformanceVideo(v),
            ) ?? []
          }
          onSaved={refresh}
          onDraft={registerDraft}
        />
      ))}
      <CompleteRoutineSection
        playlistId={performance.id}
        className="max-w-3xl"
      />
      <div className="max-w-3xl space-y-2">
        <button
          type="button"
          className={button}
          disabled={!valid || !!task?.busy || !!data?.job?.active}
          onClick={() => void generate()}
        >
          {task?.busy && task.playlistId === performance.id
            ? "Creating final video…"
            : "Generate final video"}
        </button>
        <p className="text-sm text-gray-500">
          {valid
            ? "Keep this tab open until your final video is saved."
            : "Select a valid range for every performance video to continue."}
        </p>
      </div>
    </div>
  );
}

function ClipEditor({
  playlistId,
  clip,
  index,
  videos,
  onSaved,
  onDraft,
}: {
  playlistId: string;
  clip: RoutineClip;
  index: number;
  videos: RoutineVideoChoice[];
  onSaved: () => Promise<void>;
  onDraft: (id: string, draft: ClipDraft) => void;
}) {
  const [selectedId, setVideoId] = useState(
    videos.some((v) => v.id === clip.video_id) ? clip.video_id! : "",
  );
  const videoId = selectedId || (videos.length === 1 ? videos[0]!.id : "");
  const [preview, setPreview] = useState<string | null>(null);
  const [start, setStart] = useState(
    clip.reviewed && clip.start !== null ? formatTimestamp(clip.start) : "",
  );
  const [end, setEnd] = useState(
    clip.reviewed && clip.end !== null ? formatTimestamp(clip.end) : "",
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [playerDuration, setPlayerDuration] = useState<number | null>(null);
  const player = useRef<TrimPlayerHandle>(null);
  const stopPreview = () => player.current?.stopPreview();
  const task = useRoutineTask();
  const choice = videos.find((v) => v.id === videoId);
  const source = choice?.source;
  const duration = source?.duration ?? playerDuration;
  const embed = performanceVimeoEmbed(choice?.video_url);
  const playerUrl = preview ?? (!source?.storage_path ? embed : null);
  const startSeconds = parseTimestamp(start);
  const endSeconds = parseTimestamp(end);
  const valid =
    !!choice && !!duration && !validateTrim(startSeconds, endSeconds, duration);
  const matchesSaved =
    clip.reviewed &&
    clip.video_id === videoId &&
    clip.start === startSeconds &&
    clip.end === endSeconds;
  const busy = saving || !!task?.busy;

  useEffect(() => {
    setPlayerDuration(null);
    setPlayhead(0);
  }, [videoId, choice?.video_url, source?.revision]);

  useEffect(() => {
    let alive = true;
    setPreview(null);
    if (source?.storage_path)
      void routineRequest<{ url: string }>("source_preview", {
        video_id: videoId,
        playlist_id: playlistId,
      })
        .then((r) => {
          if (alive) setPreview(r.url);
        })
        .catch((e) => {
          if (alive) setError(e.message);
        });
    return () => {
      alive = false;
    };
  }, [videoId, source?.revision]);
  const changeVideo = (id: string) => {
    setVideoId(id);
    setPlayerDuration(null);
    setStart("");
    setEnd("");
    setError(null);
    stopPreview();
  };
  const approve = async () => {
    if (!choice || !valid || !duration) {
      throw new Error(
        "Select a portion on the timeline, or choose Use full video.",
      );
    }
    setSaving(true);
    setError(null);
    try {
      const reviewedSource =
        source ??
        (
          await routineRequest<{ source: RoutineSource }>("source_reference", {
            video_id: videoId,
            playlist_id: playlistId,
            video_url: choice.video_url,
            duration,
          })
        ).source;
      await reviewRoutine(playlistId, [
        {
          entry_id: clip.entry_id,
          video_id: videoId,
          source_revision: reviewedSource.revision,
          start: startSeconds,
          end: endSeconds,
        },
      ]);
      await onSaved();
      window.dispatchEvent(new Event("bollyrhythm:routine-updated"));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Range could not be saved.");
      throw e;
    } finally {
      setSaving(false);
    }
  };
  useEffect(() => {
    onDraft(clip.entry_id, { valid, save: approve });
  }, [
    onDraft,
    clip.entry_id,
    videoId,
    start,
    end,
    duration,
    source?.revision,
    valid,
  ]);
  return (
    <article className="overflow-hidden rounded-2xl border border-[#e3dce1] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-[#321b29]">
          {index + 1}. {clip.name}
        </h3>
        <span
          className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${matchesSaved ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`}
        >
          {matchesSaved ? (
            <CheckCircle2 size={14} />
          ) : (
            <AlertCircle size={14} />
          )}
          {matchesSaved
            ? "Reviewed for this performance"
            : valid
              ? "Range selected"
              : "Trim review needed"}
        </span>
      </div>
      <div className="grid items-center gap-4 p-4 lg:grid-cols-[minmax(0,480px)_minmax(0,1fr)] lg:gap-6">
        <div className="mx-auto w-full min-w-0 max-w-[480px] space-y-2 lg:max-w-none">
          {videos.length > 1 ? (
            <label className="block text-sm font-medium">
              Performance video
              <select
                aria-label={`Final Video for ${clip.name}`}
                className={`${input} mt-2`}
                value={videoId}
                onChange={(e) => changeVideo(e.target.value)}
                disabled={busy}
              >
                <option value="">Select a performance video</option>
                {videos.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.title}
                  </option>
                ))}
              </select>
            </label>
          ) : choice ? (
            <p className="text-xs font-medium text-gray-500">{choice.title}</p>
          ) : (
            <p className="text-sm text-amber-800">
              No published performance video is available for this tutorial.
            </p>
          )}
          {playerUrl ? (
            <RoutineTrimPlayer
              key={playerUrl}
              ref={player}
              url={playerUrl}
              embedded={!preview}
              title={`Performance video for ${clip.name}`}
              onDuration={setPlayerDuration}
              onError={setError}
              onTime={setPlayhead}
            />
          ) : (
            <div className="flex aspect-video items-center justify-center rounded-xl bg-[#f6f2f5] p-6 text-center text-sm text-gray-500">
              {source?.storage_path
                ? "Loading performance video…"
                : choice
                  ? "This performance video is unavailable. Check its video link in the tutorial settings."
                  : "A performance video is needed before you can choose a range."}
            </div>
          )}
        </div>
        <div className="min-w-0 space-y-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-[#321b29]">
            <Scissors size={14} />
            Trim for this routine
          </p>
          {duration ? (
            <RoutineTrimTimeline
              name={clip.name}
              duration={duration}
              start={Number.isFinite(startSeconds) ? startSeconds : null}
              end={Number.isFinite(endSeconds) ? endSeconds : null}
              playhead={playhead}
              disabled={!choice || !playerDuration || busy}
              onInteractionStart={stopPreview}
              onChange={(from, to) => {
                setStart(formatTimestamp(from));
                setEnd(formatTimestamp(to));
                setError(null);
              }}
              onSeek={(seconds, play) => {
                void player.current?.seek(seconds, play).catch(() =>
                  setError("Could not seek to this moment. Try again once the video has loaded."),
                );
              }}
            />
          ) : (
            <p className="rounded-xl bg-[#faf7f9] p-5 text-sm text-gray-500">
              {choice ? "Loading video timeline…" : "Select a performance video to begin trimming."}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="text-xs font-semibold text-[#762a5c] disabled:opacity-50"
              disabled={!duration || busy}
              onClick={() => {
                stopPreview();
                setStart(formatTimestamp(0));
                setEnd(formatTimestamp(duration!));
              }}
            >
              Use full video
            </button>
            <button
              type="button"
              className="rounded-lg bg-[#762a5c] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              disabled={!valid || !playerUrl || !playerDuration || busy}
              onClick={() => {
                void player.current
                  ?.preview(startSeconds, endSeconds)
                  .catch(() =>
                    setError(
                      "Press Play in the performance video, then preview your range again.",
                    ),
                  );
              }}
            >
              Preview selected portion
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Saved when you click Generate final video.
          </p>
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
